import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n, LangSwitch } from "@/lib/i18n";
import { toast } from "sonner";
import { PaperPlaneTilt, Envelope } from "@phosphor-icons/react";

export default function Settings() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [mail, setMail] = useState({ enabled: false, sender: null });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (user && ["admin", "hr"].includes(user.role)) {
      api.get("/email/status").then(({ data }) => setMail(data)).catch(() => {});
    }
  }, [user]);

  const sendTest = async () => {
    const to = window.prompt("Send test email to:", user.email);
    if (!to) return;
    setSending(true);
    try {
      await api.post("/email/test", { to });
      toast.success(`Test email sent to ${to} from ${mail.sender}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-black/60">{t("settings")}</div>
      <h1 className="font-display font-black text-5xl tracking-tighter leading-none mb-6">Profile</h1>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="brut-border bg-white p-6 brut-shadow">
          <div className="text-xs font-bold uppercase tracking-wider text-black/60">{t("name")}</div>
          <div className="font-display font-black text-2xl tracking-tighter">{user.name}</div>
          <div className="mt-2 text-xs font-mono">{user.email}</div>
          <div className="mt-3 flex gap-2">
            <span className="tag bg-[#FFCC00]">{t(user.role)}</span>
            {user.team && <span className="tag bg-white">{user.team}</span>}
          </div>
        </div>
        <div className="brut-border bg-white p-6 brut-shadow">
          <div className="text-xs font-bold uppercase tracking-wider text-black/60">{t("badge")}</div>
          <div className="font-display font-black text-2xl tracking-tighter font-mono">{user.nfc_uid || "—"}</div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-black/60">{t("vacation_current_year")}</div>
              <div className="font-display font-black text-2xl tracking-tighter">{user.vacation_balance_days}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-black/60">{t("vacation_prior_year")}</div>
              <div className="font-display font-black text-2xl tracking-tighter">{user.vacation_balance_prior_year ?? 0}</div>
            </div>
          </div>
        </div>
        <div className="brut-border bg-white p-6 brut-shadow md:col-span-2">
          <div className="text-xs font-bold uppercase tracking-wider text-black/60 mb-2">Language</div>
          <LangSwitch />
        </div>

        {["admin", "hr"].includes(user.role) && (
          <div className="brut-border bg-white p-6 brut-shadow md:col-span-2" data-testid="email-settings">
            <div className="flex items-center gap-2 mb-2">
              <Envelope size={22} weight="bold" />
              <div className="font-display font-black text-xl tracking-tighter">Email delivery</div>
            </div>
            <div className="text-xs font-mono text-black/70 mb-3">
              Status: {mail.enabled ? (
                <span className="tag bg-[#00CC33]">ENABLED · sender {mail.sender}</span>
              ) : (
                <span className="tag bg-[#FF3333] text-white">DISABLED</span>
              )}
            </div>
            <div className="text-sm mb-3">
              Attendio sends leave-request emails to managers, decision emails to employees, and
              payroll exports — all from <strong>{mail.sender || "attendio@epholding.org"}</strong> via Microsoft 365.
            </div>
            <button
              data-testid="email-test-btn"
              disabled={!mail.enabled || sending}
              onClick={sendTest}
              className="bg-[#0047FF] text-white brut-border brut-shadow brut-hover px-4 py-2 font-bold uppercase tracking-wider text-xs flex items-center gap-2 disabled:opacity-50"
            >
              <PaperPlaneTilt size={16} weight="bold" /> {sending ? "Sending…" : "Send test email"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
